import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Copy, Trash2, RotateCcw, Eye, Plus } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { StatusBadge, inputCls } from "../admin/AFields";
import { pickLocalized } from "./localized";

const FILTERS = ["", "today", "draft", "published", "scheduled", "archived", "trash"];

export default function StudioLibrary() {
  const { base } = useOutletContext();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    const trash = filter === "trash";
    const params = new URLSearchParams();
    if (trash) params.set("trash", "1");
    else if (["draft", "published", "scheduled", "archived"].includes(filter)) params.set("status", filter);
    else if (filter === "today") params.set("filter", "today");
    if (q) params.set("q", q);
    api.get(`/greetings/list?${params}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="studio-library">
      <SEO title="Greeting Library" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Greeting Library</h1>
        <Link to={`${base}/create`} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="library-new-btn"><Plus size={15} /> New Greeting</Link>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`lib-filter-${f || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold capitalize transition-colors ${filter === f ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>
            {f || "All"}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title / occasion…" className={`${inputCls} !w-56`} data-testid="library-search" />
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-navy" data-testid="library-search-btn">Search</button>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((g) => (
          <div key={g.id} className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`lib-card-${g.id}`}>
            {g.image && <img src={g.image} alt="" className="aspect-square w-full rounded-md object-cover" loading="lazy" />}
            <div className="mt-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-navy">{pickLocalized(g.title)}</p>
                <p className="text-xs text-slate-500">{pickLocalized(g.occasion || g.category)} · {g.date || "—"} · {pickLocalized(g.language)}</p>
              </div>
              <StatusBadge value={filter === "trash" ? "archived" : g.status} />
            </div>
            <div className="mt-3 flex items-center gap-1.5">
              {filter === "trash" ? (
                <>
                  <button onClick={() => act(() => api.post(`/greetings/${g.id}/restore`), "Restored")} className="rounded-md border border-gray-200 p-1.5 text-brandgreen hover:border-brandgreen" aria-label="Restore" data-testid={`lib-restore-${g.id}`}><RotateCcw size={13} /></button>
                  <button onClick={() => { if (window.confirm("Delete forever? This cannot be undone.")) act(() => api.delete(`/greetings/${g.id}?permanent=1`), "Deleted permanently"); }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete forever" data-testid={`lib-purge-${g.id}`}><Trash2 size={13} /></button>
                </>
              ) : (
                <>
                  <Link to={`${base}/create?id=${g.id}`} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`lib-edit-${g.id}`}><Pencil size={13} /></Link>
                  {g.status === "published" && <a href={`/greetings/${g.slug}`} target="_blank" rel="noreferrer" className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="View" data-testid={`lib-view-${g.id}`}><Eye size={13} /></a>}
                  <button onClick={() => act(() => api.post("/greetings/save", { ...g, id: undefined, title: `${pickLocalized(g.title)} (Copy)`, status: "draft" }), "Duplicated")} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Duplicate" data-testid={`lib-dup-${g.id}`}><Copy size={13} /></button>
                  {g.status !== "published" && <button onClick={() => act(() => api.post("/greetings/save", { id: g.id, status: "published" }), "Published")} className="rounded-md bg-brandgreen-100 px-2.5 py-1.5 text-[11px] font-bold text-brandgreen" data-testid={`lib-publish-${g.id}`}>Publish</button>}
                  {g.status === "published" && <button onClick={() => act(() => api.post("/greetings/save", { id: g.id, status: "archived" }), "Unpublished (archived)")} className="rounded-md bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600" data-testid={`lib-unpublish-${g.id}`}>Unpublish</button>}
                  <button onClick={() => { if (window.confirm("Move this greeting to Trash?")) act(() => api.delete(`/greetings/${g.id}`), "Moved to Trash"); }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Trash" data-testid={`lib-trash-${g.id}`}><Trash2 size={13} /></button>
                </>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full py-12 text-center text-sm text-slate-400">No greetings in this view.</p>}
      </div>
    </div>
  );
}
